import {useEffect, useRef, useState, useSyncExternalStore} from 'react';
import {fenzsafeBackendAdapter, guestClaimBackendAdapter} from './fenzsafeBackendAdapter';
import {setFenzSafeAdapter} from './fenzsafeStore';
import {FenzSafeApp, FenzSafeCapabilities} from './FenzSafeApp';
import {SessionExpiredNotice} from './ExceptionStateNotices';
import {trackFenzSafeEntry} from './fenzsafeFunnelEvents';
import {claimPendingGuestMatters, clearGuestClaimSnapshot, cleanupGuestClaimStorage, guestClaimAvailableFor} from './fenzsafeGuestClaim';

/** Authenticated workspace. Guest data is never automatically imported into a session. */
export function FenzSafeWorkspace({capabilities, account}: {capabilities?: FenzSafeCapabilities; account?: {scope: string; label: string}} = {}) {
  const [ready, setReady] = useState(false);
  const [guestAvailable, setGuestAvailable] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [transferStatus, setTransferStatus] = useState('');
  const [transferring, setTransferring] = useState(false);
  const busy = useRef(false);
  const active = useRef(true);
  const scope = useRef(account?.scope);
  scope.current = account?.scope;
  const state = useSyncExternalStore(fenzsafeBackendAdapter.subscribe, fenzsafeBackendAdapter.getSnapshot);

  useEffect(() => {
    setFenzSafeAdapter(fenzsafeBackendAdapter);
    fenzsafeBackendAdapter.reset();
    trackFenzSafeEntry();
    setReady(true);
    active.current = true;
    void cleanupGuestClaimStorage().catch(() => {
      if (active.current) setTransferStatus('Temporary browser storage could not be checked. No guest work has been imported.');
    });
    return () => { active.current = false; };
  }, []);

  useEffect(() => {
    setConfirmed(false);
    setGuestAvailable(!!account?.scope && guestClaimAvailableFor(account.scope));
  }, [account?.scope, ready]);

  async function transfer(discard = false) {
    if (busy.current || !account || (!discard && !confirmed)) return;
    const expectedScope = account.scope;
    const isCurrent = () => active.current && scope.current === expectedScope;
    busy.current = true; setTransferring(true); setTransferStatus('');
    try {
      if (discard) await clearGuestClaimSnapshot();
      else await claimPendingGuestMatters(guestClaimBackendAdapter(expectedScope), {scope: expectedScope, confirmed: true, isCurrent});
      if (isCurrent()) {
        setGuestAvailable(guestClaimAvailableFor(expectedScope));
        setTransferStatus(discard ? 'Temporary guest work was removed from this browser.' : 'Guest transfer completed. Check your saved matters and document statuses; uploaded documents still require the normal safety checks.');
      }
    } catch (error) {
      if (isCurrent()) setTransferStatus(error instanceof Error ? error.message : 'Guest transfer is incomplete. Check your saved matters before trying again.');
    } finally { busy.current = false; if (isCurrent()) setTransferring(false); }
  }

  if (!ready) {
    return <p className="notice" role="status">Loading FenzSafe…</p>;
  }
  if (state.loadError?.type === 'sign_in_required') {
    return <SessionExpiredNotice context="your FenzSafe documents" isRealSession />;
  }
  if (state.loadError?.type === 'unavailable') {
    return (
      <section aria-label="FenzSafe could not be loaded" className="fsa-root">
        <p role="alert">{state.loadError.message}</p>
        <button type="button" className="fsa-primary" onClick={() => fenzsafeBackendAdapter.reset()}>
          Try again
        </button>
      </section>
    );
  }
  return (
    <>
      {guestAvailable && account && <section className="notice" aria-label="Save guest work to this account">
        <h2>Save your guest work</h2>
        <p>Temporary work is stored in this browser. On a shared device, it may belong to someone else. Nothing will be imported until you confirm.</p>
        <p>Save into: <strong>{account.label}</strong></p>
        <label><input type="checkbox" checked={confirmed} disabled={transferring} onChange={event => setConfirmed(event.target.checked)}/> I created this guest work and want to save it into this account.</label>
        <button type="button" disabled={!confirmed || transferring} onClick={() => void transfer()}>Save guest work into this account</button>
        <button type="button" disabled={transferring} onClick={() => void transfer(true)}>Discard temporary guest work</button>
      </section>}
      {transferStatus && <p className="notice" role="status">{transferStatus}</p>}
      <FenzSafeApp capabilities={capabilities} accountScope={account?.scope} />
    </>
  );
}
